import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCents } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { postStatusTone, postStatusLabel } from '../../components/PartnershipPosts.jsx';
import { Button, Badge, Input, Spinner, EmptyState, Alert } from '../../components/ui.jsx';

/**
 * Pay per post, the brand's side: the posts creators sent, to approve or not.
 * Approving is what pays a post — one commission at the creator's frozen
 * amount, settled and paid like any other.
 */
export default function BrandPosts() {
  const { data, loading, reload } = useFetch('/posts?side=brand');
  const [error, setError] = useState('');
  const posts = data?.posts ?? [];
  const waiting = posts.filter((p) => p.status === 'submitted');
  const answered = posts.filter((p) => p.status !== 'submitted');

  return (
    <DashboardLayout
      side="brand"
      title="Posts"
      description="Posts creators made for your pay-per-post programs. Open each one; approving it is what pays it."
    >
      <Alert className="mb-4">{error}</Alert>
      {loading ? (
        <Spinner />
      ) : posts.length === 0 ? (
        <EmptyState title="No posts yet">
          Creators on a program that pays per post send each post here for you to approve.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          <section>
            <h2 className="mb-2 text-sm font-semibold">
              Waiting for you {waiting.length > 0 && <span className="text-ink-muted">({waiting.length})</span>}
            </h2>
            {waiting.length === 0 ? (
              <p className="text-sm text-ink-muted">Nothing waiting.</p>
            ) : (
              <ul className="space-y-3">
                {waiting.map((p) => (
                  <WaitingPost key={p.id} post={p} onDone={reload} onError={setError} />
                ))}
              </ul>
            )}
          </section>

          {answered.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-semibold">Answered</h2>
              <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Creator</th>
                      <th>Post</th>
                      <th className="num">Pays</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {answered.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.creator?.displayName ?? '—'}
                          {p.program && <span className="block text-xs text-ink-faint">{p.program.title}</span>}
                        </td>
                        <td className="max-w-xs">
                          <a href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="block truncate text-accent hover:underline">
                            {p.url}
                          </a>
                        </td>
                        <td className="num">{formatCents(p.flatAmountCents, { currency: p.currency })}</td>
                        <td>
                          <Badge tone={postStatusTone[p.status]}>{postStatusLabel[p.status]}</Badge>
                          {p.decisionNote && <span className="block text-xs text-ink-muted">“{p.decisionNote}”</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

function WaitingPost({ post, onDone, onError }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const amount = formatCents(post.flatAmountCents, { currency: post.currency });

  async function decide(action) {
    setBusy(true);
    onError('');
    try {
      await api.post(`/posts/${post.id}/${action}`, { note });
      onDone();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="rounded-lg border border-rule bg-paper-raised p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {post.creator ? (
              <Link to={`/creators/${post.creator.slug}`} className="hover:text-accent">
                {post.creator.displayName}
              </Link>
            ) : (
              'Creator removed'
            )}
            {post.program && <span className="font-normal text-ink-muted"> · {post.program.title}</span>}
          </p>
          <a href={post.url} target="_blank" rel="noopener noreferrer nofollow" className="mt-0.5 block max-w-xl truncate text-sm text-accent hover:underline">
            {post.url}
          </a>
          {post.note && <p className="mt-1 text-sm text-ink-muted">“{post.note}”</p>}
        </div>
        <div className="text-right">
          <p className="font-semibold tabular-nums">{amount}</p>
          <p className="text-xs text-ink-faint">sent {new Date(post.createdAt).toLocaleDateString()}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
          placeholder="Note to the creator (optional)"
          aria-label="Note to the creator"
          className="min-w-48 flex-1"
        />
        <Button size="sm" onClick={() => decide('approve')} disabled={busy} className="h-10">
          Approve and pay {amount}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => decide('reject')} disabled={busy} className="h-10">
          Reject
        </Button>
      </div>
    </li>
  );
}
